import React, { useEffect, useRef } from 'react';
import { Line } from 'rough-viz';

// Module-scoped counter for unique DOM ids — same pattern as RoughBar.
let nextRoughLineId = 0;

export type LineSeries = {
  /** Label for the series (drives the rough-viz legend). */
  name: string;
  /** Values aligned to the x-axis from index 0. Series can have
   *  different lengths — rough-viz uses the longest as the x extent
   *  and other series simply end where their array ends. Pass plain
   *  numbers; rough-viz coerces with `+d` so null/undefined become 0
   *  (use a shorter array if you want a line to terminate early). */
  values: number[];
};

type Props = {
  /** X-axis labels (e.g. week numbers as strings). */
  xLabels: string[];
  /** One entry per line. The keying for rough-viz uses `name` as the
   *  data column name, so names must be unique. */
  series: LineSeries[];
  title?: string;
  xLabel?: string;
  yLabel?: string;
  /** One colour per series, aligned by index. Pass CSS colour strings. */
  colors?: string[];
  roughness?: number;
  /** 0 = Gaegu, 1 = Indie Flower. */
  fontStyle?: 0 | 1;
  width?: number;
  height?: number;
  legend?: boolean;
  /** Radius of the data-point bullets in pixels. rough-viz's default
   *  is 10, which crowds dense time-series. */
  bulletRadius?: number;
  /** d3-format spec for y-axis tick labels — e.g. ",.2f" for money
   *  with thousands separator and 2 decimals. Default: raw number,
   *  which can spill 10+ decimals from float division. */
  yValueFormat?: string;
  /** d3-format spec for x-axis tick labels. */
  xValueFormat?: string;
  /** Font size for axis tick labels (CSS dimension, e.g. "14px"). */
  axisFontSize?: string;
  /** Font size for hover-tooltip labels (CSS dimension, e.g. "1rem"). */
  tooltipFontSize?: string;
};

/**
 * React wrapper around rough-viz's vanilla Line chart. rough-viz's
 * Line takes per-series columns keyed `y1`, `y2`, ... and an `x`
 * column name. We pivot `series` into that shape internally so callers
 * just pass a list of series objects.
 */
const RoughLine: React.FC<Props> = ({
  xLabels,
  series,
  title,
  xLabel,
  yLabel,
  colors,
  roughness,
  fontStyle,
  width = 1100,
  height = 420,
  legend = true,
  bulletRadius,
  yValueFormat,
  xValueFormat,
  axisFontSize,
  tooltipFontSize,
}) => {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `rough-line-${++nextRoughLineId}`;
  }
  const id = idRef.current;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const host = ref.current;
    host.innerHTML = '';

    // rough-viz Line in object mode (node_modules/rough-viz/src/Line.js):
    //   - `data` is an object keyed by series name; each value is a
    //     plain `number[]`. Object.keys(data) becomes the series list
    //     and the legend.
    //   - `x` is the array of x-axis labels (NOT a column-name string).
    //     `this.x[i]` is used directly as the x position for the i-th
    //     point of each series.
    //   - No y1/y2 options in object mode.
    //
    // Series-name suffix: rough-viz's hover handler updates hover
    // text via d3 `select('.${name}classtext')`, which is a global
    // querySelector. If two charts on the same page share series
    // names (e.g. both have "FY2025"), hovering chart B mutates the
    // text inside chart A. Suffixing the name with this chart's
    // unique id avoids the collision; we strip the suffix back out
    // of the legend below so the user still sees clean names.
    const suffix = `__${id}`;
    const internalName = (display: string) => `${display}${suffix}`;
    const data: Record<string, number[]> = {};
    series.forEach((s) => {
      data[internalName(s.name)] = s.values;
    });
    const options: Record<string, unknown> = {
      element: `#${id}`,
      data,
      x: xLabels,
      title,
      xLabel,
      yLabel,
      roughness,
      font: fontStyle,
      width,
      height,
      legend,
      legendPosition: 'right',
      colors,
      stroke: '#1f2937',
      strokeWidth: 1.2,
      circleRadius: bulletRadius,
      yValueFormat,
      xValueFormat,
      axisFontSize,
      tooltipFontSize,
      // Y-axis title sits at x = -margin.left/2; bumping `left` pushes
      // it further from the tick labels so longer values (e.g. the
      // cumulative chart's "100k+" ticks) don't collide with it.
      margin: { top: 50, right: 130, bottom: 70, left: 110 },
    };

    try {
      // rough-viz mutates the options bag, hence the `any` cast.
      new Line(options as any);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RoughLine] construct failed', err, { id, options });
    }

    // Strip the per-chart suffix from any visible text. The legend
    // entries and any post-render hover tooltips render the internal
    // series name verbatim; we walk every <text> in our host and
    // rewrite the textContent to drop the suffix. Cheap — at most a
    // few dozen text nodes per chart.
    host.querySelectorAll('text').forEach((el) => {
      if (el.textContent && el.textContent.includes(suffix)) {
        el.textContent = el.textContent.split(suffix).join('');
      }
    });

    // Pin hover labels to the top of the chart area, stacked by
    // series so they don't overlap. rough-viz's own mousemove sets
    // each label's `y` to its data point — which puts every label on
    // top of every other when they're at similar values. This listener
    // runs in the bubble phase AFTER rough-viz's and overwrites `y`
    // with a per-series offset based on document order (the order
    // series were declared in the `series` prop). First series sits
    // at TOP_Y, each subsequent series drops by LINE_HEIGHT.
    const TOP_Y = 5;
    const LINE_HEIGHT = 18;
    const onMove = () => {
      host
        .querySelectorAll<SVGTextElement>('[class$="classtext"] text')
        .forEach((node, i) =>
          node.setAttribute('y', String(TOP_Y + i * LINE_HEIGHT)),
        );
    };
    host.addEventListener('mousemove', onMove);

    return () => {
      host.removeEventListener('mousemove', onMove);
      host.innerHTML = '';
    };
  }, [
    id,
    xLabels,
    series,
    title,
    xLabel,
    yLabel,
    colors,
    roughness,
    fontStyle,
    width,
    height,
    legend,
    bulletRadius,
    yValueFormat,
    xValueFormat,
    axisFontSize,
    tooltipFontSize,
  ]);

  return (
    <div
      id={id}
      ref={ref}
      style={{ minWidth: width, minHeight: height }}
    />
  );
};

export default RoughLine;
